import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    DefaultRouteColumn,
    IdColumn,
    NameColumn,
} from './columns';
import {
    Box,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import {
    getAgentEvents,
    getAllRoutes,
    getCampaignEvents,
} from '../../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import FiltersMultiSelect from '../../../common/filtersMultiSelect/FiltersMultiSelect';
import { MantineReactTable } from 'mantine-react-table';
import ResourceEventsModal from '../../../modals/events/EventsModal';
import { StatColumn } from '../../../common';
import UserRoutesTable from './userRoutesTable/UserRoutesTable';
import classes from '../../../../css/DetailPanelTable.module.css';
import { naiveRound } from '../../../../utils/text';
import { sumObjectKeys } from '../../../../utils/statUtils';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';

const RoutesView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getAllRoutes(queryParams),
        queryKey: ['routes'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: apns, pagination: dataPagination } = paginatedData || {};
    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            DefaultRouteColumn(),
            StatColumn('Sent', (x) => x.stats?.send),
            StatColumn('Clicks', (x) =>
                sumObjectKeys(x.stats, ['uniqueClick'])
            ),
            StatColumn('Previews', (x) =>
                sumObjectKeys(x.stats, ['uniquePreview'])
            ),
            StatColumn(
                'CTR',
                (x) => {
                    const sends = sumObjectKeys(x.stats, ['send']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((uniqueClicks / sends) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => x.stats?.conversion),
            StatColumn(
                'CR',
                (x) => {
                    const conversions = sumObjectKeys(x.stats, ['conversion']);
                    const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                    ]);
                    return naiveRound((conversions / uniqueClicks) * 100, 2);
                },
                true
            ),
        ],
        [apns]
    );

    const tableData = useMemo(() => (isLoading ? [] : apns), [apns, isLoading]);

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'routes-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'routes-group-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: false,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Routes',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        renderTopToolbarCustomActions: () => (
            <Box
                w={'100%'}
                px={'lg'}>
                <FiltersMultiSelect
                    options={[{ label: 'test', value: 'test' }]}
                />
            </Box>
        ),
        renderDetailPanel: ({ row, table }) => (
            <>
                <UserRoutesTable routeId={row.original.id} />
            </>
        ),
        mantineTableBodyRowProps: ({
            isDetailPanel,
            row,
            staticRowIndex,
            table,
        }) => ({
            className: isDetailPanel ? classes.rowPadding : null,
            onClick: (e) => {
                const INTERACTIVE_SELECTOR =
                    'a[href], button, input:not([type="hidden"]), select, textarea, label, [role="button"]';
                const interactiveElemnt =
                    e.target instanceof HTMLElement &&
                    e.target.closest(INTERACTIVE_SELECTOR);
                if (interactiveElemnt) return;

                if (!isDetailPanel) {
                    row.toggleExpanded();
                }
            },
        }),
        //renderRowActions: ActionsColumn,

        enableExpanding: true,
        manualPagination: true,
        manualSorting: true,
        manualPagination: true,
        manualSorting: true,

        enableEditing: false,
        enableExpandAll: false,
        positionExpandColumn: 'last',
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,
        manualExpanding: true,
        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
        </Container>
    );
};
export default RoutesView;
